'use client';

import { useState } from 'react';
import { useT } from '#/i18n/client';
import { Icons } from '../icons';
import { cn } from '#/lib/utils';
import { toast } from 'react-toastify';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '#/components/ui/tooltip';

interface CopyToClipboardProps {
  text: string;
  children?: React.ReactNode;
  className?: string;
  showIcon?: boolean;
  toastMessage?: string;
}

export function CopyToClipboard({
  text,
  children,
  className,
  showIcon = true,
  toastMessage
}: CopyToClipboardProps) {
  const t = useT('common');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(toastMessage ?? t('copy.email'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error(t('copy.fail'));
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={handleCopy}
          className={cn(
            'inline-flex items-center gap-1 transition-colors',
            className
          )}
          type="button"
        >
          {children || text}
          {showIcon && (
            <span className="ml-1">
              {copied ? (
                <Icons.check className="w-4 h-4 text-green-500" />
              ) : (
                <Icons.copy className="w-4 h-4" />
              )}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{copied ? t('copy.copied') : t('copy.click')}</p>
      </TooltipContent>
    </Tooltip>
  );
}
